<template>
    <component :is="currentLayout" />
    <Toast />
    <DynamicDialog />
    <ConfirmPopup group="popup"></ConfirmPopup>
    <!-- `closable` and `dismissableMask` are not ConfirmDialog props; they fall
         through as attrs onto the Dialog it renders, which does declare them.
         No X: the dialog already has a Cancel button, and two ways to say no
         in the same corner is one too many. Clicking the mask (or Escape)
         dismisses it, which leaves the object untouched — the same outcome as
         Cancel. Note the `reject` callback does NOT run on a mask dismiss, so
         nothing may depend on it to undo work. -->
    <ConfirmDialog
        group="dialog"
        :closable="false"
        dismissableMask
    ></ConfirmDialog>
    <div id="popups-container"></div>
    <div id="recaptcha-container"></div>
</template>

<script lang="ts" setup>
import DynamicDialog from "primevue/dynamicdialog";
import Toast from "primevue/toast";
import { computed, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";

import { useHtmlTitleManager } from "@/composables/html-title-manager";
import { useKeybindingsExplanation } from "@/composables/keybindings-explanation";
import { useRebrandNotice } from "@/composables/rebrand-notice";
import { useUnauthorizedAccessDialog } from "@/composables/unauthorized-access-dialog";
import * as Layouts from "@/layouts";

import { useHotKeys } from "./composables/hotkeys";

const route = useRoute();
const { attach } = useHotKeys();
const { open: openUnauthorizedDialog } = useUnauthorizedAccessDialog();
const { checkAndShowRebrandNotice } = useRebrandNotice();

attach();
useKeybindingsExplanation();
useHtmlTitleManager();

// Handler for unauthorized access events
const handleUnauthorizedAccess = (event: CustomEvent) => {
    openUnauthorizedDialog(event.detail);
};

// Register and clean up event listeners
onMounted(() => {
    window.addEventListener(
        "unauthorized-access",
        handleUnauthorizedAccess as EventListener
    );
    checkAndShowRebrandNotice();
});

onUnmounted(() => {
    window.removeEventListener(
        "unauthorized-access",
        handleUnauthorizedAccess as EventListener
    );
});

const currentLayout = computed(() => {
    if (route.meta.layout) {
        if (!(route.meta.layout in Layouts)) {
            throw new Error(`Unknown layout: ${route.meta.layout}`);
        }
        return Layouts[route.meta.layout];
    }
    return Layouts["ApplicationLayout"];
});
</script>

<style scoped>
#navbar-container {
    min-height: 100vh;
    overflow: auto;
}
#popups-container {
    position: absolute;
    z-index: 99;
}
</style>
